import React, { useEffect, useState } from "react";
import {
	Button,
	IconButton,
	ListItem,
	ListItemText,
	ListItemButton,
	Box,
	TextField,
	Stack,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { lprService, organizationService, associationService } from "client-app-core";
import { useSelector, useDispatch } from "react-redux";
import { openDialog, closeDialog } from "orion-components/AppState/Actions";
import { generateDirStyles } from "orion-components/SharedComponents/GenerateDirStyles/GenerateDirStyles";
import { getDir } from "orion-components/i18n/Config/selector";
import { Translate, getTranslation } from "orion-components/i18n";
import { canManageByApplication } from "orion-components/Profiles/Selectors";
import { getWatchlists, getWatchlistEntries, clearWatchlistEntries } from "../Dock/actions";
import moment from "moment";

const entryFields = [
	{ label: "plateNumber", key: "plateNumber", minWidth: 65, contentWidth: 65 },
	{ label: "dateAdded", key: "createdDate", minWidth: 71, contentWidth: 70 },
	{ label: "addedBy", key: "createdBy", minWidth: 71, contentWidth: 70 }
];

const baseStyles = {
	container: {
		paddingRight: 10,
		paddingLeft: 15,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "start",
		height: "95%"
	},
	hotListHeader: {
		fontFamily: "Roboto",
		fontStyle: "normal",
		fontWeight: 400,
		fontSize: "14px",
		lineHeight: "16px",
		color: "#FFFFFF"
	},
	hotListUIControlsHeader: { marginBottom: 10, marginRight: 10, width: "90%" },
	hotListItemContainer: {
		height: 52,
		backgroundColor: "#494D53",
		borderRadius: 5,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		flexShrink: 0,
		marginBottom: 10
	},
	hotListItemWrapper: {
		display: "flex",
		alignItems: "center",
		gap: "10px",
		flex: "1 0 0"
	},
	hotListFieldWrapper: (dir, params = {}) => {
		const includeFlex = params.includeFlex !== false;
		return {
			display: "flex",
			flexDirection: "column",
			justifyContent: "flex-end",
			alignItems: "flex-start",
			gap: "5px",
			...(includeFlex && { flex: "1 0 0" })
		};
	},

	hotListFieldHeader: {
		color: "#B5B9BE",
		fontFamily: "Roboto",
		fontSize: "10px",
		fontStyle: "normal",
		fontWeight: 400,
		lineHeight: "normal",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		overflow: "hidden"
	},
	hotListValues: {
		color: "#FFF",
		fontFamily: "Roboto",
		fontSize: "12px",
		fontStyle: "normal",
		fontWeight: 400,
		lineHeight: "normal",
		overflow: "hidden",
		width: "100%",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap"
	},
	removeButton: { display: "flex", alignItems: "center", gap: 10, color: "#fff" },
	textFieldInputProps: {
		fontFamily: "Roboto",
		fontWeight: 400,
		fontSize: "14px", // adjust based on Heading 3 spec
		lineHeight: "100%",
		letterSpacing: "0",
		textAlign: "center",
		color: "#FFF"
	},
	textFieldInputLabelProps: {
		fontFamily: "Roboto",
		fontSize: "12px",
		color: "#aaa"
	}
};

const LPRWatchlistPanel = () => {
	const dispatch = useDispatch();

	const panelRef = React.useRef(null);
	const canManageLPR = useSelector(state => canManageByApplication(state, "lpr-app", "manage"));
	const dir = useSelector(state => getDir(state));
	const locale = useSelector(state => state.i18n.locale);
	const user = useSelector(state => state.session.user);

	const dialog = useSelector(state => state.appState.dialog.openDialog);
	const watchlists = useSelector(state => state.appState.dock.dockData.watchlists);
	const entries = useSelector(state => state.appState.dock.dockData.watchlistEntries);

	const styles = generateDirStyles(baseStyles, dir);

	const [uploadStatus, setUploadStatus] = useState("");
	const [uploadResult, setUploadResult] = useState(null);
	const [uploading, setUploading] = useState(false);
	const handleLPRUpload = event => {
		setUploadStatus("");
		const file = event.target.files[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = e => {
			try {
				// send csv text to lpr app for processing
				const csvText = e.target.result;
				setUploading(true);
				lprService.addFromCSV(csvText, (err, result) => {
					if (err) {
						console.error("Error while adding entries from CSV", err);
						setUploadStatus("Failure");
						setUploading(false);
					} else {
						if (result.success === false) {
							setUploadStatus("Failure");
						} else if (result.duplicateEntries > 0) {
							setUploadStatus("DuplicateEntries");
							setUploadResult(result);
						} else {
							setUploadStatus("Success");
							setUploadResult(result);
						}
						setUploading(false);
						dispatch(getWatchlists());
					}
				});
			} catch (err) {
				// display error somewhere
				console.error("Error while loading CSV", err);
				setUploadStatus("Failure");
				setUploading(false);
			}
		};
		reader.readAsText(file);
	};

	//This function handles the dynamic passing of arguments to generateDirStyles.
	const getHotListWrapperStyle = index =>
		generateDirStyles(
			{
				wrapper: baseStyles.hotListFieldWrapper(dir, {
					includeFlex: index !== entryFields.length - 1
				})
			},
			dir
		).wrapper;

	const [showWatchlistEntries, setShowWatchlistEntries] = useState(false);
	const [currentWatchlist, setCurrentWatchlist] = useState(null);
	const [currentEntry, setCurrentEntry] = useState(null);
	const [error, setError] = useState("");
	const [errorContext, setErrorContext] = useState("");
	const [newName, setNewName] = useState("");
	const [newDesc, setNewDesc] = useState("");
	const [plateNum, setPlateNum] = useState("");
	const [plateState, setPlateState] = useState("");
	const [orgUserNames, setOrgUserNames] = useState(null);
	useEffect(() => {
		try {
			organizationService.getOrgUsers(user.profile.orgId, (err, response) => {
				if (err) {
					console.error(err);
				} else {
					setOrgUserNames(new Map(response.map(({ id, name }) => [id, name])));
				}
			});
		} catch (err) {
			console.error(err);
		}
		dispatch(getWatchlists());
	}, []);

	useEffect(() => {
		// Clears the entry fields and any errors/status messages on switching between the watchlists/entries panels
		setPlateNum("");
		setPlateState("");
		setNewName("");
		setNewDesc("");
		setError("");
		setErrorContext("");
		setUploadStatus("");
	}, [showWatchlistEntries]);

	const selectWatchlist = watchlist => {
		setCurrentWatchlist(watchlist);
		dispatch(getWatchlistEntries(watchlist.id));
		setShowWatchlistEntries(true);
	};

	const addNewWatchlist = (name, description) => {
		if (!name || !name.trim()) {
			setError("emptyWatchlistName");
			setErrorContext("newWatchlistName");
			return;
		}
		setError("");
		setErrorContext("");
		lprService.addWatchlist(name.trim(), description.trim(), (err, res) => {
			if (err) {
				setError(err.reason);
				setErrorContext("newWatchlistName");
			} else {
				if (res.success === false) {
					setError(res.reason.message);
					setErrorContext("newWatchlistName");
				} else {
					// Clear fields after successfully submitting
					setNewName("");
					setNewDesc("");
					dispatch(getWatchlists());
				}
			}
		});
	};

	const addNewWatchlistEntry = (plateNumber, state) => {
		if (!plateNumber || !plateNumber.trim()) {
			setError("emptyPlateNumber");
			setErrorContext("plateNumberEntry");
			return;
		}
		if (!state || !state.trim()) {
			setError("emptyStateProvince");
			setErrorContext("stateEntry");
			return;
		}
		setError("");
		setErrorContext("");
		const watchlistId = currentWatchlist.id;
		lprService.addWatchlistEntry(watchlistId, plateNumber.trim(), state.trim(), (err, res) => {
			if (err) {
				setError(err.reason);
				setErrorContext("plateNumberEntry");
			} else {
				if (res.success === false) {
					setError(res.reason.message);
					setErrorContext("plateNumberEntry");
				} else {
					// Clear fields after successfully submitting
					setPlateNum("");
					setPlateState("");
					dispatch(getWatchlistEntries(watchlistId));
				}
			}
		});
	};

	const openDeleteEntryDialog = entry => {
		setCurrentEntry(entry);
		dispatch(openDialog("confirmDeleteEntry"));
	};

	const deleteEntry = id => {
		if (!id) {
			console.error("Missing ID for deletion");
			return;
		}
		lprService.removeEntryFromWatchlist(currentWatchlist.id, id, (err, res) => {
			if (err) {
				console.error(err.message, err);
			} else {
				dispatch(closeDialog("confirmDeleteEntry"));
				setCurrentEntry(null);
				dispatch(getWatchlistEntries(currentWatchlist.id));
			}
		});
	};

	const onClose = () => {
		setCurrentWatchlist(null);
		dispatch(clearWatchlistEntries());
		setShowWatchlistEntries(false);
	};

	const [updatedName, setUpdatedName] = useState("");
	const [updatedDesc, setUpdatedDesc] = useState("");

	const openEditDialog = watchlist => {
		setCurrentWatchlist(watchlist);
		setUpdatedName(watchlist.name);
		setUpdatedDesc(watchlist.description);
		dispatch(openDialog("editWatchlist"));
	};

	const updateWatchlist = (id, newName, newDescription = "") => {
		if (!newName || !newName.trim()) {
			setError("emptyWatchlistName");
			setErrorContext("updatedWatchlistName");
			return;
		}
		setError("");
		setErrorContext("");
		lprService.updateWatchlist(id, newName.trim().toLowerCase(), newDescription.trim(), (err, res) => {
			if (err) {
				setError(err.reason);
				setErrorContext("updatedWatchlistName");
			} else {
				if (res.success === false) {
					setError(res.reason.message);
					setErrorContext("updatedWatchlistName");
				} else {
					dispatch(closeDialog("editWatchlist"));
					dispatch(getWatchlists());
				}
			}
		});
	};

	const [canDeleteWatchlist, setCanDeleteWatchlist] = useState(true);

	const openDeleteWatchlistDialog = watchlist => {
		setCurrentWatchlist(watchlist);
		associationService.checkAssociations(watchlist.id, (err, result) => {
			if (err) {
				console.error(err);
			} else {
				const { hasAssociations } = result;
				setCanDeleteWatchlist(hasAssociations === 0);
			}
		});
		dispatch(openDialog("deleteWatchlist"));
	};

	const deleteWatchlist = id => {
		if (!id) {
			console.error("Missing ID for deletion");
			return;
		}
		lprService.deleteWatchlist(id, (err, res) => {
			if (err) {
				console.error(err.message, err);
			} else {
				dispatch(closeDialog("deleteWatchlist"));
				dispatch(getWatchlists());
			}
		});
	};

	const watchlistsList = watchlists => {
		return (
			<>
				{canManageLPR && (
					<>
						<div style={styles.hotListUIControlsHeader}>
							<TextField
								label={getTranslation("global.sharedComponents.LPRWatchlistPanel.nameEntry")}
								value={newName}
								error={errorContext === "newWatchlistName"}
								helperText={
									errorContext === "newWatchlistName" && error
										? getTranslation(`global.sharedComponents.LPRWatchlistPanel.error.${error}`)
										: ""
								}
								onChange={event => {
									setNewName(event.target.value);
									if (errorContext === "newWatchlistName") {
										setErrorContext("");
										setError("");
									}
								}}
								variant="standard"
								fullWidth
								InputProps={{
									sx: {
										...styles.textFieldInputProps,
										"& input": {
											padding: "0px 0px 10px 0px"
										},
										"&:before": {
											borderBottom: "1px solid #949597"
										},
										"&:after": {
											borderBottom: "1px solid #42a5f5"
										},
										"&:hover:not(.Mui-disabled):before": {
											borderBottom: "1px solid #42a5f5"
										}
									},
									autoComplete: "off"
								}}
								InputLabelProps={{
									sx: styles.textFieldInputLabelProps
								}}
								sx={{
									"& .MuiSelect-select.MuiSelect-standard.MuiInputBase-input.MuiInput-input": {
										textAlign: "left !important"
									},

									"& .MuiSelect-icon": {
										color: "#fff"
									},
									input: {
										color: "#fff",
										fontFamily: "Roboto",
										fontSize: "12px",
										fontStyle: "normal",
										fontWeight: 400,
										lineHeight: "normal"
									},
									label: {
										color: "rgba(255, 255, 255, 0.3)",
										fontFamily: "Roboto",
										fontSize: "12px",
										fontStyle: "normal",
										fontWeight: 400,
										lineHeight: "normal"
									}
								}}
							/>
							<TextField
								label={getTranslation("global.sharedComponents.LPRWatchlistPanel.description")}
								value={newDesc}
								onChange={event => {
									setNewDesc(event.target.value);
								}}
								variant="standard"
								fullWidth
								InputProps={{
									sx: {
										...styles.textFieldInputProps,
										"& input": {
											padding: "0px 0px 10px 0px"
										},
										"&:before": {
											borderBottom: "1px solid #949597"
										},
										"&:after": {
											borderBottom: "1px solid #42a5f5"
										},
										"&:hover:not(.Mui-disabled):before": {
											borderBottom: "1px solid #42a5f5"
										}
									},
									autoComplete: "off"
								}}
								InputLabelProps={{
									sx: styles.textFieldInputLabelProps
								}}
								sx={{
									"& .MuiSelect-select.MuiSelect-standard.MuiInputBase-input.MuiInput-input": {
										textAlign: "left !important"
									},

									"& .MuiSelect-icon": {
										color: "#fff"
									},
									input: {
										color: "#fff",
										fontFamily: "Roboto",
										fontSize: "12px",
										fontStyle: "normal",
										fontWeight: 400,
										lineHeight: "normal"
									},
									label: {
										color: "rgba(255, 255, 255, 0.3)",
										fontFamily: "Roboto",
										fontSize: "12px",
										fontStyle: "normal",
										fontWeight: 400,
										lineHeight: "normal"
									}
								}}
							/>
						</div>
						<Box display="flex" fullWidth justifyContent="center" maxHeight="40px" style={{ gap: "1rem" }}>
							<Button
								onClick={() => addNewWatchlist(newName, newDesc)}
								variant="contained"
								sx={{
									width: "150px",
									height: "29px",
									backgroundColor: "#4CB5F4",
									fontSize: "12px",
									fontWeight: 700,
									fontFamily: "Roboto",
									borderRadius: "5px",
									alignSelf: "center",
									color: "#FFFFFF",
									":hover": {
										backgroundColor: "#42a5f5"
									},
									textTransform: "uppercase"
								}}
							>
								<Translate value="global.sharedComponents.LPRWatchlistPanel.addWatchlist" />
							</Button>
							<Button
								component="label"
								variant="contained"
								loading={uploading}
								sx={{
									width: "150px",
									height: "29px",
									backgroundColor: "#4CB5F4",
									fontSize: "12px",
									fontWeight: 700,
									fontFamily: "Roboto",
									borderRadius: "5px",
									alignSelf: "center",
									color: "#FFFFFF",
									":hover": {
										backgroundColor: "#42a5f5"
									},
									textTransform: "uppercase"
								}}
							>
								<Translate value="global.sharedComponents.LPRWatchlistPanel.uploadWatchlistEntries" />
								<input
									type="file"
									multiple
									accept=".csv"
									style={{ display: "none" }}
									onChange={handleLPRUpload}
								/>
							</Button>
						</Box>
						<p
							style={{
								color:
									uploadStatus === "Success"
										? "#4CAF50"
										: uploadStatus === "DuplicateEntries"
											? "#FFC107"
											: "#f44336",
								textAlign: "center",
								fontSize: "12px",
								height: "25px"
							}}
						>
							{uploadStatus
								? getTranslation(
										`global.sharedComponents.LPRWatchlistPanel.upload${uploadStatus}`,
										uploadResult?.duplicateEntries
									)
								: ""}
						</p>
					</>
				)}
				<div
					style={{
						marginTop: 15,
						marginBottom: 15,
						maxHeight: "95%",
						width: "90%",
						overflowY: "auto",
						alignItems: "center"
					}}
				>
					{watchlists?.length > 0 ? (
						watchlists
							.sort((a, b) => {
								const nameA = a.name.toUpperCase(); // ignore upper and lowercase
								const nameB = b.name.toUpperCase(); // ignore upper and lowercase
								return nameA.localeCompare(nameB);
							})
							.map(item => (
								<ListItem
									style={styles.hotListItemContainer}
									sx={
										canManageLPR && {
											".MuiListItemSecondaryAction-root": {
												opacity: 0,
												visibility: "hidden",
												transition: "opacity 0.2s ease, visibility 0.2s ease"
											},
											"&:hover .MuiListItemSecondaryAction-root": {
												opacity: 1,
												visibility: "visible"
											}
										}
									}
									secondaryAction={
										canManageLPR && (
											<div style={{ display: "flex", gap: "4px" }}>
												<IconButton
													style={{ color: "#fff" }}
													aria-label="edit"
													onClick={e => {
														e.stopPropagation();
														openEditDialog(item);
													}}
												>
													<EditIcon />
												</IconButton>
												<IconButton
													sx={{
														color: "#fff",
														"&.Mui-disabled": {
															color: "#ffffff4d"
														}
													}}
													aria-label="delete"
													onClick={e => {
														e.stopPropagation();
														openDeleteWatchlistDialog(item);
													}}
												>
													<DeleteIcon />
												</IconButton>
											</div>
										)
									}
								>
									<ListItemButton onClick={() => selectWatchlist(item)}>
										<ListItemText primary={item.name} />
									</ListItemButton>
								</ListItem>
							))
					) : (
						<div style={{ padding: 20, textAlign: "center", color: "white" }}>
							{getTranslation("global.sharedComponents.LPRWatchlistPanel.noWatchlists")}
						</div>
					)}
				</div>
				{canManageLPR && (
					<>
						<Dialog
							open={dialog === "editWatchlist"}
							PaperProps={{
								sx: {
									backgroundColor: "#41454a",
									padding: "20px",
									borderRadius: 0,
									width: "450px",
									height: "300px"
								}
							}}
						>
							<DialogTitle>
								<Typography style={{ color: "#fff" }}>
									{getTranslation(
										"global.sharedComponents.LPRWatchlistPanel.dialog.editWatchlistTitle"
									)}
								</Typography>
							</DialogTitle>
							<DialogContent>
								<Box
									fullWidth
									display="flex"
									flexDirection="column"
									alignItems="center"
									gap="1rem"
									padding="12px"
								>
									<TextField
										fullWidth
										variant="standard"
										label={getTranslation("global.sharedComponents.LPRWatchlistPanel.nameEntry")}
										value={updatedName}
										error={errorContext === "updatedWatchlistName"}
										helperText={
											errorContext === "updatedWatchlistName" && error
												? getTranslation(
														`global.sharedComponents.LPRWatchlistPanel.error.${error}`
													)
												: ""
										}
										onChange={event => {
											setUpdatedName(event.target.value);
											if (errorContext === "updatedWatchlistName") {
												setErrorContext("");
												setError("");
											}
										}}
										InputProps={{
											sx: {
												...styles.textFieldInputProps,
												"& input": {
													padding: "0px 0px 10px 0px"
												},
												"&:before": {
													borderBottom: "1px solid #949597"
												},
												"&:after": {
													borderBottom: "1px solid #42a5f5"
												},
												"&:hover:not(.Mui-disabled):before": {
													borderBottom: "1px solid #42a5f5"
												}
											},
											autoComplete: "off"
										}}
										InputLabelProps={{
											sx: styles.textFieldInputLabelProps
										}}
										sx={{
											"& .MuiSelect-select.MuiSelect-standard.MuiInputBase-input.MuiInput-input":
												{
													textAlign: "left !important"
												},

											"& .MuiSelect-icon": {
												color: "#fff"
											},
											input: {
												color: "#fff",
												fontFamily: "Roboto",
												fontSize: "12px",
												fontStyle: "normal",
												fontWeight: 400,
												lineHeight: "normal"
											},
											label: {
												color: "rgba(255, 255, 255, 0.3)",
												fontFamily: "Roboto",
												fontSize: "12px",
												fontStyle: "normal",
												fontWeight: 400,
												lineHeight: "normal"
											}
										}}
									/>
									<TextField
										fullWidth
										variant="standard"
										label={getTranslation("global.sharedComponents.LPRWatchlistPanel.description")}
										value={updatedDesc}
										onChange={event => {
											setUpdatedDesc(event.target.value);
										}}
										InputProps={{
											sx: {
												...styles.textFieldInputProps,
												"& input": {
													padding: "0px 0px 10px 0px"
												},
												"&:before": {
													borderBottom: "1px solid #949597"
												},
												"&:after": {
													borderBottom: "1px solid #42a5f5"
												},
												"&:hover:not(.Mui-disabled):before": {
													borderBottom: "1px solid #42a5f5"
												}
											},
											autoComplete: "off"
										}}
										InputLabelProps={{
											sx: styles.textFieldInputLabelProps
										}}
										sx={{
											"& .MuiSelect-select.MuiSelect-standard.MuiInputBase-input.MuiInput-input":
												{
													textAlign: "left !important"
												},

											"& .MuiSelect-icon": {
												color: "#fff"
											},
											input: {
												color: "#fff",
												fontFamily: "Roboto",
												fontSize: "12px",
												fontStyle: "normal",
												fontWeight: 400,
												lineHeight: "normal"
											},
											label: {
												color: "rgba(255, 255, 255, 0.3)",
												fontFamily: "Roboto",
												fontSize: "12px",
												fontStyle: "normal",
												fontWeight: 400,
												lineHeight: "normal"
											}
										}}
									/>
								</Box>
							</DialogContent>
							<DialogActions>
								<Button
									style={{ height: 45, color: "#fff" }}
									color="primary"
									onClick={() => {
										dispatch(closeDialog("editWatchlist"));
										setCurrentWatchlist(null);
									}}
								>
									<Translate value="global.sharedComponents.LPRWatchlistPanel.dialog.cancel" />
								</Button>
								<Button
									style={{ height: 45 }}
									variant="contained"
									color="primary"
									sx={{
										textTransform: "uppercase",
										color: "#fff",
										":hover": {
											backgroundColor: "#42a5f5"
										},
										"&.Mui-disabled": {
											color: "#ffffff4d"
										}
									}}
									onClick={() => updateWatchlist(currentWatchlist.id, updatedName, updatedDesc)}
									disabled={
										!updatedName.trim() ||
										updatedName.trim().toLowerCase() === currentWatchlist?.name
									}
								>
									<Translate value="global.sharedComponents.LPRWatchlistPanel.dialog.save" />
								</Button>
							</DialogActions>
						</Dialog>
						<Dialog
							open={dialog === "deleteWatchlist"}
							PaperProps={{
								sx: {
									backgroundColor: "#41454a",
									padding: "20px",
									borderRadius: 0,
									width: "350px",
									height: "225px"
								}
							}}
						>
							<DialogContent>
								<Typography style={{ color: "#fff" }}>
									{canDeleteWatchlist
										? getTranslation(
												"global.sharedComponents.LPRWatchlistPanel.dialog.deleteWatchlistText",
												currentWatchlist?.name
											)
										: getTranslation(
												"global.sharedComponents.LPRWatchlistPanel.dialog.cannotDeleteWatchlist"
											)}
								</Typography>
							</DialogContent>
							<DialogActions>
								{canDeleteWatchlist && (
									<Button
										style={{ height: 45, color: "#fff" }}
										variant="contained"
										sx={{
											backgroundColor: "#f44336",
											textTransform: "uppercase",
											":hover": {
												backgroundColor: "#E95858"
											}
										}}
										onClick={() => deleteWatchlist(currentWatchlist.id)}
									>
										<Translate value="global.sharedComponents.LPRWatchlistPanel.dialog.confirm" />
									</Button>
								)}
								<Button
									style={{ height: 45, color: canDeleteWatchlist ? "#fff" : "#42a5f5" }}
									color="primary"
									onClick={() => {
										dispatch(closeDialog("deleteWatchlist"));
										setCurrentWatchlist(null);
									}}
								>
									{canDeleteWatchlist
										? getTranslation("global.sharedComponents.LPRWatchlistPanel.dialog.cancel")
										: getTranslation("global.sharedComponents.LPRWatchlistPanel.dialog.back")}
								</Button>
							</DialogActions>
						</Dialog>
					</>
				)}
			</>
		);
	};

	const entriesList = entries => {
		return (
			<>
				<p
					style={{
						...styles.hotListHeader,
						alignSelf: locale === "ar" || locale === "ar_kw" ? "end" : "start"
					}}
				>
					<IconButton
						aria-label="back"
						onClick={onClose}
						sx={{
							paddingRight: "5px",
							color: "#fff"
						}}
					>
						{locale === "ar" || locale === "ar_kw" ? <ArrowForwardIosIcon /> : <ArrowBackIosIcon />}
					</IconButton>
					{getTranslation("global.sharedComponents.LPRWatchlistPanel.entriesHeader") + currentWatchlist.name}
				</p>
				{canManageLPR && (
					<Stack direction="row" spacing={1} fullWidth sx={{ justifyContent: "space-between" }}>
						<TextField
							label={getTranslation(`global.sharedComponents.LPRWatchlistPanel.plateEntry`)}
							value={plateNum}
							error={errorContext === "plateNumberEntry"}
							helperText={
								errorContext === "plateNumberEntry" && error
									? getTranslation(`global.sharedComponents.LPRWatchlistPanel.error.${error}`)
									: ""
							}
							onChange={event => {
								setPlateNum(event.target.value);
								if (errorContext === "plateNumberEntry") {
									setErrorContext("");
									setError("");
								}
							}}
							variant="standard"
							InputProps={{
								sx: {
									...styles.textFieldInputProps,
									"& input": {
										padding: "0px 0px 10px 0px"
									},
									"&:before": {
										borderBottom: "1px solid #949597"
									},
									"&:after": {
										borderBottom: "1px solid #42a5f5"
									},
									"&:hover:not(.Mui-disabled):before": {
										borderBottom: "1px solid #42a5f5"
									}
								},
								autoComplete: "off"
							}}
							InputLabelProps={{
								sx: styles.textFieldInputLabelProps
							}}
							sx={{
								input: {
									color: "#fff",
									fontFamily: "Roboto",
									fontSize: "12px",
									fontStyle: "normal",
									fontWeight: 400,
									lineHeight: "normal"
								},
								label: {
									color: "rgba(255, 255, 255, 0.3)",
									fontFamily: "Roboto",
									fontSize: "12px",
									fontStyle: "normal",
									fontWeight: 400,
									lineHeight: "normal"
								},
								flex: 1
							}}
						/>
						<TextField
							label={getTranslation(`global.sharedComponents.LPRWatchlistPanel.plateState`)}
							value={plateState}
							error={errorContext === "stateEntry"}
							helperText={
								errorContext === "stateEntry" && error
									? getTranslation(`global.sharedComponents.LPRWatchlistPanel.error.${error}`)
									: ""
							}
							onChange={event => {
								setPlateState(event.target.value);
								if (errorContext === "stateEntry") {
									setErrorContext("");
									setError("");
								}
							}}
							variant="standard"
							InputProps={{
								sx: {
									...styles.textFieldInputProps,
									"& input": {
										padding: "0px 0px 10px 0px"
									},
									"&:before": {
										borderBottom: "1px solid #949597"
									},
									"&:after": {
										borderBottom: "1px solid #42a5f5"
									},
									"&:hover:not(.Mui-disabled):before": {
										borderBottom: "1px solid #42a5f5"
									}
								},
								autoComplete: "off"
							}}
							InputLabelProps={{
								sx: styles.textFieldInputLabelProps
							}}
							sx={{
								input: {
									color: "#fff",
									fontFamily: "Roboto",
									fontSize: "12px",
									fontStyle: "normal",
									fontWeight: 400,
									lineHeight: "normal"
								},
								label: {
									color: "rgba(255, 255, 255, 0.3)",
									fontFamily: "Roboto",
									fontSize: "12px",
									fontStyle: "normal",
									fontWeight: 400,
									lineHeight: "normal"
								},
								"& .MuiFormLabel-asterisk": {
									color: "#E85858",
									fontFamily: "Roboto",
									fontSize: "12px",
									fontStyle: "normal",
									fontWeight: 400,
									lineHeight: "normal",
									leadingTrim: "both",
									textEdge: "cap"
								},
								flex: 1
							}}
						/>
						<Button
							onClick={() => addNewWatchlistEntry(plateNum, plateState)}
							variant="contained"
							sx={{
								width: "80px",
								height: "35px",
								backgroundColor: "#4CB5F4",
								fontSize: "12px",
								fontWeight: 700,
								fontFamily: "Roboto",
								borderRadius: "5px",
								alignSelf: "center",
								color: "#FFFFFF",
								":hover": {
									backgroundColor: "#42a5f5"
								},
								textTransform: "uppercase",
								lineHeight: "1.2"
							}}
						>
							<Translate value="global.sharedComponents.LPRWatchlistPanel.addEntry" />
						</Button>
					</Stack>
				)}
				{entries?.length > 0 ? (
					<div
						style={{
							marginTop: 15,
							marginBottom: 15,
							maxHeight: "95%",
							width: "90%",
							overflowY: "auto",
							alignItems: "center"
						}}
					>
						{entries
							.sort((a, b) => {
								const plateA = a.plateNumber.toUpperCase(); // ignore upper and lowercase
								const plateB = b.plateNumber.toUpperCase();
								return plateA.localeCompare(plateB);
							})
							.map((entry, index) => (
								<ListItem
									style={{ ...styles.hotListItemContainer, padding: "8px 50px 10px 10px" }}
									sx={
										canManageLPR && {
											".MuiListItemSecondaryAction-root": {
												opacity: 0,
												visibility: "hidden",
												transition: "opacity 0.2s ease, visibility 0.2s ease"
											},
											"&:hover .MuiListItemSecondaryAction-root": {
												opacity: 1,
												visibility: "visible"
											}
										}
									}
									key={index}
									secondaryAction={
										canManageLPR && (
											<IconButton
												sx={styles.removeButton}
												onClick={() => openDeleteEntryDialog(entry)}
											>
												<DeleteIcon />
											</IconButton>
										)
									}
								>
									<div style={styles.hotListItemWrapper}>
										{entryFields.map((field, fieldIndex) => (
											<div
												key={field.key}
												style={{
													...getHotListWrapperStyle(fieldIndex),
													minWidth: field.minWidth,
													width: field.width
												}}
											>
												<div style={{ ...styles.hotListFieldHeader, width: field.width }}>
													{getTranslation(
														`global.sharedComponents.LPRWatchlistPanel.${field.label}`
													)}
												</div>
												<div style={{ ...styles.hotListValues, width: field.contentWidth }}>
													{field.label === "dateAdded"
														? moment(entry[field.key])
																.locale(locale)
																.format("MM/DD/YYYY") || "N/A"
														: field.label === "addedBy"
															? orgUserNames.get(entry[field.key])
															: entry[field.key]}
												</div>
											</div>
										))}
									</div>
								</ListItem>
							))}
					</div>
				) : (
					<div style={{ padding: 20, textAlign: "center", color: "white" }}>
						{getTranslation("global.sharedComponents.LPRWatchlistPanel.noWatchlistEntries")}
					</div>
				)}
				{canManageLPR && (
					<Dialog
						open={dialog === "confirmDeleteEntry"}
						PaperProps={{
							sx: {
								backgroundColor: "#41454a",
								padding: "20px",
								borderRadius: 0,
								width: "350px",
								height: "225px"
							}
						}}
					>
						<DialogContent>
							<Typography style={{ color: "#fff" }}>
								{getTranslation("global.sharedComponents.LPRWatchlistPanel.dialog.deleteEntryText")}
							</Typography>
						</DialogContent>
						<DialogActions>
							<Button
								style={{ height: 45, color: "#fff" }}
								variant="contained"
								sx={{
									backgroundColor: "#f44336",
									textTransform: "uppercase",
									":hover": {
										backgroundColor: "#E95858"
									}
								}}
								onClick={() => deleteEntry(currentEntry.id)}
							>
								<Translate value="global.sharedComponents.LPRWatchlistPanel.dialog.confirm" />
							</Button>
							<Button
								style={{ height: 45, color: "#fff" }}
								color="primary"
								onClick={() => {
									dispatch(closeDialog("confirmDeleteEntry"));
									setCurrentEntry(null);
								}}
							>
								<Translate value="global.sharedComponents.LPRWatchlistPanel.dialog.cancel" />
							</Button>
						</DialogActions>
					</Dialog>
				)}
			</>
		);
	};

	return (
		<div style={styles.container} ref={panelRef}>
			{showWatchlistEntries ? entriesList(entries) : watchlistsList(watchlists)}
		</div>
	);
};

export default LPRWatchlistPanel;
